// Modules
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
// Components
import { LogoutConfirmationDialogComponent } from '@app/general/private/components/logout-confirmation-dialog/logout-confirmation-dialog.component';
import { JwtHelperService } from '@auth0/angular-jwt';
import { select, Store } from '@ngrx/store';
// Services
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';
import { NavigationService } from '@shared-module/services/navigation/navigation.service';
// Constants
import { APP_NAVIGATION_MENU, APP_PATHS_CONSTANTS } from '@shared/constants';
// Enums
import { PermissionsEnum } from '@shared/enums/permissions';
// Interfaces
import { INavigationItem, INavigationMenu } from '@shared/interfaces';
// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';
import * as GeneralSelectors from '@store/selectors/general.selectors';
import { Subscription } from 'rxjs';
// Rxjs
import { delay } from 'rxjs/operators';

@Component({
  selector: 'sbi-cot-back-dashboard-wrapper',
  templateUrl: './dashboard-wrapper.component.html',
  styleUrls: ['./dashboard-wrapper.component.scss'],
  standalone: false,
})
export class DashboardWrapperComponent implements OnInit, OnDestroy {
  // Public selectors
  public loggedUser$ = this._store$.pipe(
    select(AuthSelectors.selectLoggedUser)
  );
  public currentNavigation$ = this._store$.pipe(
    delay(0),
    select(GeneralSelectors.selectCurrentPage)
  );

  public env: string = '';

  // Public variables
  public loadingModule: boolean = false;
  public navigationMenu: INavigationMenu[] = [];
  public changePasswordPath: string = APP_PATHS_CONSTANTS.PASSWORD_CHANGE;
  public viewProfilePath: string = APP_PATHS_CONSTANTS.VIEW_PROFILE;
  public envFlag: string;
  public isMenuCollapsed: boolean = false;

  // Private variables
  private _subscriptions$: Subscription[] = [];
  private _helper = new JwtHelperService();
  private _userPermissions: string[] = [];

  // Constructor
  constructor(
    private _app: AppConfigService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _store$: Store<AppState>,
    private _matDialog: MatDialog,
    private _navigationService: NavigationService
  ) {}

  // On component init
  ngOnInit() {
    this._buildMenu();
    this._onRouterChange();
    this._getMenuMemoryAndEnv();
    this._checkPermissions();
  }

  // On component destroy
  ngOnDestroy() {
    this._subscriptions$.forEach(sub => sub.unsubscribe());
    this.navigationMenu = [];
  }

  // Private method to build the menu
  private _buildMenu(): void {
    this.navigationMenu = [...APP_NAVIGATION_MENU];
    this.loggedUser$.subscribe(user => {
      if (user) {
        const decodedToken = this._helper.decodeToken(user.accessToken);
        this._userPermissions = decodedToken
          ? decodedToken.Permissions
            ? decodedToken.Permissions.split(',')
            : []
          : [];
      }
    });
  }

  private _checkPermissions() {
    for (const l of this.navigationMenu) {
      l.activated = true;
      for (const a of l.navigationItems) {
        a.activated = true;
      }
    }
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Sistema_Edicion_Parametros
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Carga de datos').activated =
        false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Coeficientes').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(
          i => i.title == 'Coeficientes corredores'
        ).activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Parámetros').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Vehículos').activated = false;
    }
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Sistema_Coberturas_Paquetes
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Coberturas').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Paquetes').activated = false;
    }

    if (!this._userPermissions.includes(PermissionsEnum.Sistema_Cotizaciones)) {
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Cotizaciones').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Automóviles')
        .navigationItems.find(i => i.title == 'Fallidas').activated = false;
    }

    this.navigationMenu.find(n => n.title == 'Automóviles').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Automóviles').navigationItems
      );

    // Automobile pricing rules
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Gestion_Tarifacion_Visualizar_Reglas_Globales
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Gestión de tarifa automóviles')
        .navigationItems.find(
          i => i.title == 'Configuración de tarifas'
        ).activated = false;
    }

    // Automobile pricing rules show module
    this.navigationMenu.find(
      n => n.title == 'Gestión de tarifa automóviles'
    ).activated = !this._allItemsFalse(
      this.navigationMenu.find(n => n.title == 'Gestión de tarifa automóviles')
        .navigationItems
    );

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Usuarios_Administrar_Usuarios
      ) &&
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Usuarios_Administrar_Roles_Permisos
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Usuarios')
        .navigationItems.find(i => i.title == 'Usuarios').activated = false;
    }
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Usuarios_Administrar_Usuarios
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Usuarios')
        .navigationItems.find(i => i.title == 'Crear usuario').activated =
        false;
    }

    this.navigationMenu.find(n => n.title == 'Usuarios').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Usuarios').navigationItems
      );

    if (!this._userPermissions.includes(PermissionsEnum.Fianzas_Aprobar_Cupo)) {
      this.navigationMenu
        .find(n => n.title == 'Fianza')
        .navigationItems.find(i => i.title == 'Cupos').activated = false;
    }
    if (
      !this._userPermissions.includes(PermissionsEnum.Fianzas_Aprobar_Poliza)
    ) {
      this.navigationMenu
        .find(n => n.title == 'Fianza')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
    }

    this.navigationMenu.find(n => n.title == 'Fianza').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Fianza').navigationItems
      );

    if (!this._userPermissions.includes(PermissionsEnum.Siniestros_Ingreso)) {
      this.navigationMenu
        .find(n => n.title == 'Siniestros')
        .navigationItems.find(i => i.title == 'Automóviles').activated = false;
    }
    if (!this._userPermissions.includes(PermissionsEnum.Siniestros_Consulta)) {
      this.navigationMenu
        .find(n => n.title == 'Siniestros')
        .navigationItems.find(i => i.title == 'Transporte').activated = false;
    }
    this.navigationMenu.find(n => n.title == 'Siniestros').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Siniestros').navigationItems
      );

    if (!this._userPermissions.includes(PermissionsEnum.Crear_Campanias)) {
      this.navigationMenu
        .find(n => n.title == 'Campañas')
        .navigationItems.find(i => i.title == 'Campañas').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Campañas')
        .navigationItems.find(i => i.title == 'Crear campaña').activated =
        false;

      this.navigationMenu.find(n => n.title == 'Campañas').activated =
        !this._allItemsFalse(
          this.navigationMenu.find(n => n.title == 'Campañas').navigationItems
        );
    }

    if (!this._userPermissions.includes(PermissionsEnum.Gestion_Cuentas)) {
      this.navigationMenu
        .find(n => n.title == 'Gestión de cuentas')
        .navigationItems.find(i => i.title == 'Cuentas').activated = false;

      this.navigationMenu
        .find(n => n.title == 'Gestión de cuentas')
        .navigationItems.find(i => i.title == 'Archivadas').activated = false;

      this.navigationMenu.find(n => n.title == 'Gestión de cuentas').activated =
        !this._allItemsFalse(
          this.navigationMenu.find(n => n.title == 'Gestión de cuentas')
            .navigationItems
        );
    }

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Cartera_Seguimiento_Polizas
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Cartera')
        .navigationItems.find(i => i.title == 'Cartera').activated = false;
    }

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Cartera_Seguimiento_Saldos_Vencimientos
      )
    ) {
      const menuCartera = this.navigationMenu
        .find(n => n.title == 'Cartera')
        .navigationItems.find(i => i.title == 'Saldos');
      if (menuCartera) menuCartera.activated = false;
    }

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Cartera_Seguimiento_Saldos_Vencimientos
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Cartera')
        .navigationItems.find(i => i.title == 'Saldos').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Cartera')
        .navigationItems.find(i => i.title == 'Pagos fallidos').activated =
        false;
    }

    this.navigationMenu.find(n => n.title == 'Cartera').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Cartera').navigationItems
      );

    //Renewals
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Renovaciones_Automoviles
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Renovaciones')
        .navigationItems.find(i => {
          return i.title == 'Automóviles';
        }).activated = false;
    }

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Multiproducto
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Renovaciones')
        .navigationItems.find(i => {
          return i.title == 'Multiseguros';
        }).activated = false;
    }

    //Rent Guarantees
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Renovaciones_Garantia_Alquiler
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Renovaciones')
        .navigationItems.find(i => {
          return i.title == 'Garantía de alquiler';
        }).activated = false;
    }

    this.navigationMenu.find(n => n.title == 'Renovaciones').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Renovaciones').navigationItems
      );

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Multiseguros_Administrar_Productos_Paquetes_Seguimiento_Polizas
      ) &&
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Multiseguros_Crear_Editar_Productos_Paquetes
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Multiseguros')
        .navigationItems.find(i => {
          return i.title == 'Productos';
        }).activated = false;
    }
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Multiseguros_Administrar_Productos_Paquetes_Seguimiento_Polizas
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Multiseguros')
        .navigationItems.find(i => {
          return i.title == 'Pólizas';
        }).activated = false;
    }
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Multiseguros_Crear_Editar_Productos_Paquetes
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Multiseguros')
        .navigationItems.find(i => {
          return i.title == 'Crear productos';
        }).activated = false;
    }

    this.navigationMenu.find(n => n.title == 'Multiseguros').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Multiseguros').navigationItems
      );

    // Transport Policies
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Transporte_ReadPolicy
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(i => i.title == 'Emisión fallida').activated =
        false;
    }

    // Transport Shipments
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Transporte_ReadShippment
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(i => i.title == 'Embarques').activated = false;
    }

    // Transport Billing
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Transporte_BillShippment
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(i => i.title == 'Facturación').activated = false;
    }

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Transporte_BillShippment
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(i => i.title == 'Facturación fallida').activated =
        false;
    }

    // Transport Categories Configuration
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Transporte_Configuracion_Categorias
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Transporte')
        .navigationItems.find(
          i => i.title == 'Configuración de categorías'
        ).activated = false;
    }

    // Transport show module
    this.navigationMenu.find(n => n.title == 'Transporte').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Transporte').navigationItems
      );

    // Rent Guarantee show list of policies and quotes
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_GarantiaAlquiler_Seguimiento_Polizas_Cotizaciones
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Garantía de alquiler')
        .navigationItems.find(
          i => i.title == 'Listado de solicitudes'
        ).activated = false;

      this.navigationMenu
        .find(n => n.title == 'Garantía de alquiler')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
    }

    // Rent Guarantee show module
    this.navigationMenu.find(n => n.title == 'Garantía de alquiler').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Garantía de alquiler')
          .navigationItems
      );

    // Travel show list of policies
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Viajes_Seguimiento_Polizas_Cotizaciones
      )
    ) {
      this.navigationMenu
        .find(n => n.title == 'Viajes')
        .navigationItems.find(i => i.title == 'Pólizas').activated = false;
    }

    // Travel show module
    this.navigationMenu.find(n => n.title == 'Viajes').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Viajes').navigationItems
      );

    // Settings
    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Configuracion_Excepciones_Chasis
      )
    )
      this.navigationMenu
        .find(n => n.title == 'Configuraciones')
        .navigationItems.find(i => i.title == 'Excepción de chasis').activated =
        false;

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Configuracion_Excepciones_Documento
      )
    )
      this.navigationMenu
        .find(n => n.title == 'Configuraciones')
        .navigationItems.find(
          i => i.title == 'Excepción de documento'
        ).activated = false;

    if (
      !this._userPermissions.includes(
        PermissionsEnum.Administrador_Configuracion_Documentos_Producto
      )
    )
      this.navigationMenu
        .find(n => n.title == 'Configuraciones')
        .navigationItems.find(
          i => i.title == 'Documentos por producto'
        ).activated = false;

    // Settings show module
    this.navigationMenu.find(n => n.title == 'Configuraciones').activated =
      !this._allItemsFalse(
        this.navigationMenu.find(n => n.title == 'Configuraciones')
          .navigationItems
      );
  }

  private _allItemsFalse(arrayItems): boolean {
    for (const i of arrayItems) {
      if (i.activated) {
        return false;
      }
    }
    return true;
  }

  // Método para traer memoria del menu y cargarlo
  private _getMenuMemoryAndEnv(): void {
    const memory = JSON.parse(sessionStorage.getItem(this._app.menuMemoryKey));
    this.env = this._app.generalConfig.environment;
    if (memory) {
      memory.forEach((el, i) => {
        if (el.collapsable) this.navigationMenu[i].collapsed = el.collapsed;
      });
    }

    if (this.env == 'dev') {
      this.envFlag = 'assets/images/env-flag-develop.svg';
    } else if (this.env == 'test') {
      switch (this._resolveTestSubEnvironment()) {
        case 'scrum':
          this.envFlag = 'assets/images/env-flag-SCR.svg';
          break;
        case 'kanban':
          this.envFlag = 'assets/images/env-flag-KAN.svg';
          break;
        case 'kanban2':
          this.envFlag = 'assets/images/env-flag-KAN2.svg';
          break;
        default:
          this.envFlag = 'assets/images/env-flag-testing.svg';
          break;
      }
    } else if (this.env == 'uat') {
      this.envFlag = 'assets/images/env-flag-uat.svg';
    } else {
      this.envFlag = '';
    }
  }

  private _resolveTestSubEnvironment(): 'scrum' | 'kanban2' | 'kanban' | null {
    const url = this._app.generalConfig.authBaseUrl?.toLowerCase() ?? '';
    if (url.includes('scrum')) return 'scrum';
    if (url.includes('kanban2')) return 'kanban2'; // antes que 'kanban'
    if (url.includes('kanban')) return 'kanban';
    return null;
  }

  // Private method to watch router changes
  private _onRouterChange(): void {
    this._subscriptions$.push(
      this._router.events.subscribe(val => {
        // Clear navigation status on NavigationEnd event
        if (val instanceof NavigationEnd) {
          this._navigationService.clearNavigation();
        }

        // Set loading status on RouteConfigLoadStart
        if (val instanceof RouteConfigLoadStart) {
          this.loadingModule = true;
        }

        // Remove loading status on RouteConfigLoadEnd
        if (val instanceof RouteConfigLoadEnd) {
          this.loadingModule = false;
        }
      })
    );
  }

  // Public method to collapse sections of the navigation menu
  public toggleCollapseStatusNavigationSection(index: number): void {
    if (this.navigationMenu[index].collapsable) {
      this.navigationMenu.forEach((el, i) => {
        if (i !== index && el.collapsable) {
          el.collapsed = true;
        }
      });

      this.navigationMenu[index].collapsed =
        !this.navigationMenu[index].collapsed;

      const menuMemory = JSON.stringify(
        this.navigationMenu.map(el => ({
          collapsed: el.collapsed,
          collapsable: el.collapsable,
        }))
      );
      sessionStorage.setItem(this._app.menuMemoryKey, menuMemory);
    }
  }

  // Public method to select a current navigation item
  public navigateTo(item: INavigationItem): void {
    this._router.navigate([item.route], { relativeTo: this._route });
  }

  // Public method to open the logout confirmation dialog
  public onLogoutRequest(): void {
    this._matDialog.open(LogoutConfirmationDialogComponent, {
      panelClass: 'logout-dialog-panel',
    });
  }
}
